import * as React from 'react';
import { observer } from 'mobx-react';
import { ITranslationsCollection, translate } from 'elc-service';
import { Heading4 } from 'elc-typography';
import { Overlay } from 'elc-overlay';
import { Row } from 'elc-grid';
import { RemoveIcon } from 'elc-icons';
import { ContentSpacing, breakpoint } from 'elc-base-theme';
import styled from 'styled-components';
import { ProductShadesSidebarContent } from './ProductShadesSidebarContent';
import { IInventoryStatusMessageConfig } from '../common/InventoryStatusMessage';
import {
    IProductFullAddToBagButtonConfig,
    IProductCTAProps
} from '../../../exported/product-cta/ProductCTA';
import { IAsset } from '../../domain/entities/Asset';
import { Sku } from '../../domain/entities/Sku';
import { ProductShadesSidebarViewController } from '../../controllers/ProductShadesSidebarViewController';
import { SpinnerContainer } from '../common/SpinnerContainer';
import { ShadeOverlayHeaderTheme } from '../../../theme/default-theme';
import { classes } from '../../utils/Classes';
import { diContainer, serviceNames } from '../../../service-setup/diContainer';

export interface IProductShadesSidebarConfig
    extends IInventoryStatusMessageConfig,
        IProductFullAddToBagButtonConfig {
    productFullAddToBagProps?: IProductCTAProps;
    modelAndSmooshImages: IAsset[];
    selectedSku: Sku;
    autoReplenishOptions: number[];
    isButtonModal?: boolean;
    ctaType?: string;
}

export interface IProductShadesSidebarProps {
    className?: string;
    translations?: ITranslationsCollection;
    config: IProductShadesSidebarConfig;
    onShadeSelect?(name: string): void;
}

const ProductShadesViewWrapper = styled.div`
    .elc-overlay-sidebar {
        width: 100%;
        z-index: 9999;
    }

    ${breakpoint('desktop')`
        margin-top: ${ContentSpacing.space24};
        display: inline-block;

        .elc-overlay-sidebar {
            width: auto;
        }
    `}
`;

const OverlayWrapper = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    padding-bottom: ${ContentSpacing.space80}; /* allowance for sticky add to bag CTA */
    ${breakpoint('desktop')`
        padding-bottom: 0;
    `}
`;

const ProductShadesSidebarHeader = styled(Row)`
    align-items: center;
    padding: ${ContentSpacing.space8} ${ContentSpacing.space24};
    justify-content: space-between;
    [dir='rtl'] & {
        flex-direction: row-reverse;
    }
`;

const OverlayHeader = styled(Heading4)`
    ${ShadeOverlayHeaderTheme};
`;

const ProductShadesViewCloseButton = styled.button`
    cursor: pointer;
    width: auto;
    margin: ${ContentSpacing.space8};
    float: right;
    [dir='rtl'] & {
        float: left;
    }
`;

@translate(['selectYourShadeLabel', 'closeLabel'])
@observer
class ProductShadesSidebar extends React.Component<IProductShadesSidebarProps> {
    public static displayName: string;
    private closeButton: React.RefObject<HTMLElement>;
    private isSidebarOpen: boolean = false;
    private viewController: ProductShadesSidebarViewController;

    constructor(props: IProductShadesSidebarProps) {
        super(props);
        this.closeButton = React.createRef();
        this.viewController = diContainer.get(serviceNames.productShadesSidebarViewController);
        this.viewController.initialize(props);
    }

    public async componentDidMount() {
        this.isSidebarOpen = this.viewController.data.isProductShadesViewOpen;
    }

    public handleOverlayClose = () => {
        this.viewController.onCloseProductShadesView();
    };

    private handleShadeSelect = (name: string) => {
        this.viewController.handleShadeSelect(name);

        if (this.props.onShadeSelect) {
            this.props.onShadeSelect(name);
        }
    };

    public componentDidUpdate() {
        const {
            data: { isProductShadesViewOpen }
        } = this.viewController;

        if (isProductShadesViewOpen) {
            document.body.classList.add('product-shades-sidebar-active');
        } else {
            document.body.classList.remove('product-shades-sidebar-active');
        }

        if (this.isSidebarOpen !== isProductShadesViewOpen && this.closeButton.current) {
            this.isSidebarOpen = isProductShadesViewOpen;
            this.closeButton.current.focus();
        }
    }

    public render() {
        if (this.viewController.data.isLoading) {
            return <SpinnerContainer isLoading />;
        }

        const translations = this.props.translations as ITranslationsCollection;
        const { selectYourShadeLabel, closeLabel } = translations;
        const { data } = this.viewController;
        const config = this.props.config;

        const { productFullAddToBagProps, modelAndSmooshImages, autoReplenishOptions } = config;

        const {
            isProductShadesViewOpen,
            shades,
            shadesByColorFamily,
            selectedSku,
            largeImages
        } = data;
        const shadeSidebarContentClassNames = classes('sidebar-content');
        const productShadesViewWrapperClassNames = classes('product-shades-view-wrapper');
        const productShadesViewCloseButtonClassNames = classes('product-shades-view-close-button');
        const productShadesViewCloseButtonIconClassNames = classes(
            'product-shades-view-close-button-icon'
        );

        return (
            <ProductShadesViewWrapper>
                {this.viewController.data.isLoaded && (
                    <Overlay
                        className={productShadesViewWrapperClassNames}
                        hideOverlay={this.handleOverlayClose}
                        isOverlayVisible={isProductShadesViewOpen}
                    >
                        {isProductShadesViewOpen && (
                            <OverlayWrapper>
                                <ProductShadesSidebarHeader>
                                    <OverlayHeader>{selectYourShadeLabel}</OverlayHeader>
                                    <ProductShadesViewCloseButton
                                        className={productShadesViewCloseButtonClassNames}
                                        onClick={this.handleOverlayClose}
                                        aria-label={`${closeLabel} ${selectYourShadeLabel}`}
                                        title={`${closeLabel} ${selectYourShadeLabel}`}
                                        innerRef={this.closeButton}
                                    >
                                        <RemoveIcon
                                            className={productShadesViewCloseButtonIconClassNames}
                                        />
                                    </ProductShadesViewCloseButton>
                                </ProductShadesSidebarHeader>
                                <ProductShadesSidebarContent
                                    className={shadeSidebarContentClassNames}
                                    selectedShade={selectedSku.selectedShade}
                                    productFullAddToBagProps={productFullAddToBagProps}
                                    onShadeSelect={this.handleShadeSelect}
                                    colorFamilies={shadesByColorFamily}
                                    modelAndSmooshImages={modelAndSmooshImages}
                                    shades={shades}
                                    config={config}
                                    selectedSku={selectedSku}
                                    autoReplenishOptions={autoReplenishOptions}
                                    largeImages={largeImages}
                                />
                            </OverlayWrapper>
                        )}
                    </Overlay>
                )}
            </ProductShadesViewWrapper>
        );
    }
}

ProductShadesSidebar.displayName = 'ProductShadesSidebar';

export default ProductShadesSidebar;
